import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import DesignServicesRoundedIcon from '@mui/icons-material/DesignServicesRounded'
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import CloudDoneOutlinedRoundedIcon from '@mui/icons-material/CloudDoneOutlined'
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded'
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import LeaderboardRoundedIcon from '@mui/icons-material/LeaderboardRounded'
import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded'
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import type { ComponentType } from 'react'

export type EmptyHubStatePresetKey =
  | 'meetings'
  | 'forms'
  | 'upload'
  | 'design'
  | 'automation'
  | 'quota'
  | 'budget'
  | 'invites'
  | 'evidence'
  | 'audit'
  | 'reports'
  | 'stock'
  | 'ownerTemplates'
  | 'chat'
  | 'aiHistory'
  | 'aiPulse'
  | 'leaderboard'
  | 'forYou'
  | 'heatmap'
  | 'explore'
  | 'devices'
  | 'reviewQueueClear'
  | 'syncNothingWaiting'
  | 'workspaceRequired'

export type EmptyHubStatePreset = {
  title: string
  description: string
  Icon: ComponentType<SvgIconProps>
}

export const EMPTY_HUB_STATE_PRESETS: Record<EmptyHubStatePresetKey, EmptyHubStatePreset> = {
  meetings: { title: 'No meetings yet', description: 'Scheduled meetings and live sessions will appear here once the workspace has them.', Icon: ScheduleRoundedIcon },
  forms: { title: 'No forms yet', description: 'Create or import a form to start collecting structured responses from the team.', Icon: FactCheckRoundedIcon },
  upload: { title: 'No uploads yet', description: 'Files, batches, chat attachments, and linked artifacts will collect here.', Icon: CloudUploadRoundedIcon },
  design: { title: 'Your canvas is ready', description: 'Start with a tool, a template, or the command palette.', Icon: DesignServicesRoundedIcon },
  automation: { title: 'No workflows yet', description: 'Pinned workflow drafts and active automations will appear here.', Icon: AccountTreeRoundedIcon },
  quota: { title: 'No quotas yet', description: 'Create a quota to track output, pace, and team attainment.', Icon: SpeedRoundedIcon },
  budget: { title: 'No budget entries yet', description: 'Expenses, approvals, and budget signals will appear once activity starts.', Icon: ReceiptLongRoundedIcon },
  invites: { title: 'No invite activity yet', description: 'Pending and resolved invitations will appear after you invite members or helpers.', Icon: GroupAddRoundedIcon },
  evidence: { title: 'No evidence yet', description: 'Proof uploads and verification artifacts will be indexed here.', Icon: FolderOpenRoundedIcon },
  audit: { title: 'No activity yet', description: 'Workspace activity will appear after people create, update, upload, or export records.', Icon: SecurityRoundedIcon },
  reports: { title: 'No reports yet', description: 'Generated reports and exports will appear after the first report run.', Icon: BarChartRoundedIcon },
  stock: { title: 'No stock counts pending', description: 'Helper-submitted stock count reviews will appear here.', Icon: Inventory2RoundedIcon },
  ownerTemplates: { title: 'No templates match', description: 'Published, draft, and archived template registry rows will appear here.', Icon: ManageSearchRoundedIcon },
  chat: { title: 'No messages yet', description: 'Send the first message to start the workspace conversation.', Icon: ChatBubbleOutlineRoundedIcon },
  aiHistory: { title: 'No AI history yet', description: 'Saved Line sessions will appear after the first AI exchange.', Icon: HistoryRoundedIcon },
  aiPulse: { title: 'No pulse reports yet', description: 'Generated AI pulse reports will appear when the workspace has activity to summarize.', Icon: AutoAwesomeRoundedIcon },
  leaderboard: { title: 'No leaderboard data yet', description: 'Scores and completion momentum will appear once the team starts completing work.', Icon: LeaderboardRoundedIcon },
  forYou: { title: 'Nothing assigned yet', description: 'Assigned work, forms, and execution actions will appear here.', Icon: AssignmentRoundedIcon },
  heatmap: { title: 'No contributions yet', description: 'Contribution intensity will appear after workspace activity is recorded.', Icon: GridOnRoundedIcon },
  explore: { title: 'No templates found', description: 'Try a broader search or check back when the template registry is populated.', Icon: SearchOffRoundedIcon },
  devices: { title: 'No device update data', description: 'Published app build details will appear when the update manifest is reachable.', Icon: DevicesRoundedIcon },
  reviewQueueClear: { title: 'Review queue clear', description: 'No checklist items are waiting for review.', Icon: FactCheckRoundedIcon },
  syncNothingWaiting: { title: 'Nothing waiting', description: 'Queued changes will appear here when this device is offline.', Icon: CloudDoneOutlinedRoundedIcon },
  workspaceRequired: { title: 'No workspace selected', description: 'Pick or join a workspace to see its analysis here.', Icon: BarChartRoundedIcon },
}
